/**
 * RFC-110: File Preview Component
 *
 * Universal file preview component that routes to appropriate viewer
 * based on file type.
 */
import { useState, useEffect } from 'react'
import type { ViewerScreenshot, ViewerAttachment } from '@deepguide-ai/types'
import {
  FileText,
  FileSpreadsheet,
  Video,
  Image as ImageIcon,
  Maximize2,
  Code2,
  Loader2,
  Copy,
  Check,
} from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@deepguide-ai/ui/button'
import { ScrollArea } from '@deepguide-ai/ui/scroll-area'
import { Dialog, DialogContent } from '@deepguide-ai/ui/dialog'
import { DocxPreview } from './DocxPreview'
import { MarkdownPreview } from './MarkdownPreview'
import { PdfViewer } from './PdfViewer'
import { SpreadsheetPreview } from './SpreadsheetPreview'

export type FileType =
  | 'pdf'
  | 'image'
  | 'docx'
  | 'xlsx'
  | 'video'
  | 'markdown'
  | 'text'
  | 'other'

/**
 * Detect file type from MIME type and filename
 */
export function getFileType(mimeType: string, filename: string): FileType {
  const lowerFilename = filename.toLowerCase()

  if (mimeType === 'application/pdf' || lowerFilename.endsWith('.pdf')) {
    return 'pdf'
  }
  if (
    mimeType.startsWith('image/') ||
    /\.(png|jpg|jpeg|gif|webp|svg)$/i.test(filename)
  ) {
    return 'image'
  }
  if (mimeType.startsWith('video/') || /\.(mp4|webm|mov)$/i.test(filename)) {
    return 'video'
  }
  if (
    mimeType ===
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    lowerFilename.endsWith('.docx') ||
    lowerFilename.endsWith('.doc')
  ) {
    return 'docx'
  }
  if (
    mimeType ===
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    mimeType === 'application/vnd.ms-excel' ||
    lowerFilename.endsWith('.xlsx') ||
    lowerFilename.endsWith('.xls')
  ) {
    return 'xlsx'
  }
  if (mimeType === 'text/markdown' || /\.(md|markdown|mdx)$/i.test(filename)) {
    return 'markdown'
  }
  if (
    mimeType === 'application/json' ||
    mimeType.startsWith('text/') ||
    /\.(json|log|txt|csv|yaml|yml|xml|toml|ini|cfg|conf|sh|env)$/i.test(
      filename
    )
  ) {
    return 'text'
  }
  return 'other'
}

/**
 * Get icon for file type
 */
export function getFileIcon(type: FileType) {
  switch (type) {
    case 'pdf':
    case 'docx':
    case 'markdown':
      return <FileText className='h-4 w-4' />
    case 'image':
      return <ImageIcon className='h-4 w-4' />
    case 'video':
      return <Video className='h-4 w-4' />
    case 'xlsx':
      return <FileSpreadsheet className='h-4 w-4' />
    case 'text':
      return <Code2 className='h-4 w-4' />
    default:
      return <FileText className='h-4 w-4' />
  }
}

interface FilePreviewProps {
  evidenceFileId?: string
  file: ViewerAttachment | ViewerScreenshot | { url: string; filename?: string }
  type: FileType
  highlightParagraphs?: number[]
  highlightText?: string
  pageNumber?: number | null
  highlightBox?: { x: number; y: number; width: number; height: number } | null
  /** Forwarded to MarkdownPreview: intercept evidence-file links (e.g. inside a
   *  procedure run-summary) and open the in-app preview instead of the raw URL. */
  onEvidenceFileClick?: (href: string) => void
}

export function FilePreview({
  file,
  evidenceFileId,
  type,
  highlightParagraphs,
  highlightText,
  pageNumber,
  highlightBox,
  onEvidenceFileClick,
}: FilePreviewProps) {
  const [isFullscreen, setIsFullscreen] = useState(false)
  const url = file.url
  const filename =
    ('filename' in file ? file.filename : undefined) ?? 'Screenshot'

  switch (type) {
    case 'image':
      return (
        <>
          <div className='flex h-full min-h-0 w-full min-w-0 items-center justify-center overflow-hidden'>
            <div className='group relative flex h-full w-full items-center justify-center overflow-hidden'>
              <img
                src={url}
                alt={filename}
                className='h-full max-h-full w-full max-w-full rounded-sm object-contain'
              />
              {highlightBox && (
                <div
                  className='pointer-events-none absolute rounded-sm border-2 border-destructive bg-destructive/10'
                  style={{
                    left: `${highlightBox.x}%`,
                    top: `${highlightBox.y}%`,
                    width: `${highlightBox.width}%`,
                    height: `${highlightBox.height}%`,
                  }}
                />
              )}
              {/* Maximize button - centered overlay on image */}
              <Button
                variant='secondary'
                size='icon'
                className='absolute top-1/2 left-1/2 h-12 w-12 -translate-x-1/2 -translate-y-1/2 rounded-full opacity-0 shadow-lg transition-opacity group-hover:opacity-100'
                onClick={() => setIsFullscreen(true)}
              >
                <Maximize2 className='h-5 w-5' />
              </Button>
            </div>
          </div>

          {/* Fullscreen Modal */}
          <Dialog open={isFullscreen} onOpenChange={setIsFullscreen}>
            <DialogContent
              className='flex h-screen !max-h-screen w-screen !max-w-screen items-center justify-center border-0 bg-black/90 p-0'
              closeClassName='text-white'
            >
              <div className='relative flex items-center justify-center'>
                <img
                  src={url}
                  alt={filename}
                  className='max-h-[90vh] max-w-[90vw] rounded-md object-contain'
                />
                {highlightBox && (
                  <div
                    className='pointer-events-none absolute rounded-sm border-2 border-destructive bg-destructive/10'
                    style={{
                      left: `${highlightBox.x}%`,
                      top: `${highlightBox.y}%`,
                      width: `${highlightBox.width}%`,
                      height: `${highlightBox.height}%`,
                    }}
                  />
                )}
                {/* <Button
                  variant="secondary"
                  size="icon"
                  className="absolute top-4 right-4 shadow-md"
                  onClick={() => setIsFullscreen(false)}
                >
                  <X className="h-4 w-4" />
                </Button> */}
              </div>
            </DialogContent>
          </Dialog>
        </>
      )

    case 'video':
      return <VideoPreview url={url} />

    case 'pdf':
      return (
        <div className='h-full min-h-0 w-full'>
        <PdfViewer
          url={url}
          filename={filename}
          pageNumber={pageNumber}
          highlightText={highlightText}
          highlightBox={highlightBox}
        />
        </div>
      )

    case 'docx':
      return (
        <div className='h-full min-h-0 w-full'>
        <DocxPreview
          url={url}
          filename={filename}
          highlightParagraphs={highlightParagraphs}
          highlightText={highlightText}
        />
        </div>
      )

    case 'xlsx':
      return (
        <div className='h-full min-h-0 w-full'>
          <SpreadsheetPreview url={url} filename={filename} />
        </div>
      )

    case 'markdown':
      return (
        <div className='h-full min-h-0 w-full'>
          <MarkdownPreview url={url} filename={filename} evidenceFileId={evidenceFileId} onEvidenceFileClick={onEvidenceFileClick} />
        </div>
      )

    case 'text':
      return (
        <div className='h-full min-h-0 w-full'>
          <TextPreview url={url} filename={filename} />
        </div>
      )

    default:
      return (
        <div className='bg-muted/20 flex h-full w-full flex-col items-center justify-center gap-4 rounded-md p-8'>
          <FileText className='text-muted-foreground h-8 w-8' />
          <span className='text-muted-foreground text-sm'>{filename}</span>
          <Button variant='outline' size='sm' asChild>
            <a href={url} download={filename}>
              Download
            </a>
          </Button>
        </div>
      )
  }
}

/**
 * Videos are served through the authenticated `/api/storage/object` route,
 * which 302-redirects to a short-lived presigned URL. A bare
 * `<video src={url}>` proved unreliable across that hop (cross-origin
 * credentials + redirect + range requests), so fetch the bytes the same way
 * TextPreview/MarkdownPreview authenticate (`credentials: 'include'` +
 * `inline=1` streaming) and play from an object URL — fully seekable once
 * loaded, no auth edge cases.
 */
function VideoPreview({ url }: { url: string }) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let revoked: string | null = null
    let cancelled = false
    setObjectUrl(null)
    setError(null)
    const inlineUrl = url.includes('?') ? `${url}&inline=1` : `${url}?inline=1`
    fetch(inlineUrl, { credentials: 'include' })
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to load video (${res.status})`)
        return res.blob()
      })
      .then((blob) => {
        if (cancelled) return
        revoked = URL.createObjectURL(blob)
        setObjectUrl(revoked)
      })
      .catch((err) => {
        if (!cancelled) setError(err.message)
      })
    return () => {
      cancelled = true
      if (revoked) URL.revokeObjectURL(revoked)
    }
  }, [url])

  if (error) {
    return (
      <div className='flex h-full w-full items-center justify-center'>
        <p className='text-sm text-destructive'>{error}</p>
      </div>
    )
  }
  if (!objectUrl) {
    return (
      <div className='flex h-full w-full flex-col items-center justify-center gap-2'>
        <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
        <p className='text-xs text-muted-foreground'>Loading video…</p>
      </div>
    )
  }
  return (
    <div className='flex h-full w-full items-center justify-center'>
      <video
        src={objectUrl}
        controls
        className='max-h-full w-full max-w-full rounded-sm object-contain'
      >
        Your browser does not support the video tag.
      </video>
    </div>
  )
}

function TextPreview({
  url,
  filename,
}: {
  url: string
  filename: string
}) {
  const [content, setContent] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [copied, setCopied] = useState(false)
  const isJson = /\.json$/i.test(filename)

  const handleCopy = async () => {
    if (content === null) return
    try {
      await navigator.clipboard.writeText(content)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      toast.error('Failed to copy')
    }
  }

  useEffect(() => {
    setLoading(true)
    setError(null)
    const inlineUrl = url.includes('?') ? `${url}&inline=1` : `${url}?inline=1`
    fetch(inlineUrl, { credentials: 'include' })
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to load file (${res.status})`)
        return res.text()
      })
      .then((text) => {
        if (isJson) {
          try {
            setContent(JSON.stringify(JSON.parse(text), null, 2))
          } catch {
            setContent(text)
          }
        } else {
          setContent(text)
        }
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false))
  }, [url, isJson])

  if (loading) {
    return (
      <div className='text-muted-foreground flex h-full w-full items-center justify-center gap-2 text-sm'>
        <Loader2 className='h-4 w-4 animate-spin' />
        Loading {filename}…
      </div>
    )
  }

  if (error) {
    return (
      <div className='bg-muted/20 flex h-full w-full flex-col items-center justify-center gap-4 rounded-md p-8'>
        <FileText className='text-muted-foreground h-8 w-8' />
        <span className='text-sm text-status-red'>{error}</span>
        <Button variant='outline' size='sm' asChild>
          <a href={url} download={filename}>
            Download
          </a>
        </Button>
      </div>
    )
  }

  return (
    <div className='border-border bg-card text-card-foreground flex h-full w-full flex-col overflow-hidden rounded-md border'>
      <div className='border-border bg-surface-subtle flex h-[50px] shrink-0 items-center justify-between border-b px-4'>
        <span className='text-text-secondary min-w-0 flex-1 truncate text-xs font-medium'>
          {filename}
        </span>
        <Button
          variant='ghost'
          size='sm'
          className='text-text-subtle hover:text-text-secondary h-8 shrink-0 text-[10px]'
          onClick={handleCopy}
        >
          {copied ? (
            <Check className='mr-1 h-3.5 w-3.5' />
          ) : (
            <Copy className='mr-1 h-3.5 w-3.5' />
          )}
          {copied ? 'Copied' : 'Copy'}
        </Button>
        <Button
          variant='ghost'
          size='sm'
          className='text-text-subtle hover:text-text-secondary h-8 shrink-0 text-[10px]'
          asChild
        >
          <a href={url} download={filename}>
            Download
          </a>
        </Button>
      </div>
      <ScrollArea className='min-h-0 flex-1' type='hover'>
        <pre className='text-text-body p-4 font-mono text-[12px] leading-5 break-words whitespace-pre-wrap'>
          {content}
        </pre>
      </ScrollArea>
    </div>
  )
}
